import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Users, ChevronDown, Check, User, Heart, Baby, UserCircle2 } from 'lucide-react';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
    DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

/**
 * Round 33 — Reusable profile picker.
 *
 * Fetches /api/profiles once on mount, surfaces them in a dropdown, and
 * notifies the parent via `onChange(profileId)` when a selection is made.
 * Pre-selects the default profile on first load.
 *
 * Props:
 *   value     — current selected profile_id (null = default)
 *   onChange  — (profile_id, profile_object) => void
 *   testIdPrefix — string prefix for data-testids (default 'profile-picker')
 *   compact   — boolean, render a smaller version (used in modals)
 */
export default function ProfilePicker({ value, onChange, testIdPrefix = 'profile-picker', compact = false }) {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get('/profiles')
            .then(({ data }) => {
                if (!alive) return;
                const rows = data.profiles || [];
                setProfiles(rows);
                // Auto-select default on first mount when no value provided
                if (!value && rows.length > 0) {
                    const def = rows.find(p => p.is_default) || rows[0];
                    onChange?.(def.id, def);
                }
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selected = profiles.find(p => p.id === value)
        || profiles.find(p => p.is_default)
        || profiles[0];

    if (loading) {
        return (
            <div className={`${compact ? 'h-10' : 'h-12'} flex items-center px-3 text-sm text-slate-500`}
                 data-testid={`${testIdPrefix}-loading`}>
                Loading profiles…
            </div>
        );
    }
    if (profiles.length === 0) {
        return (
            <div className={`${compact ? 'h-10' : 'h-12'} flex items-center px-3 text-sm text-slate-500`}
                 data-testid={`${testIdPrefix}-empty`}>
                No profiles yet. Add one in Profile settings.
            </div>
        );
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    data-testid={`${testIdPrefix}-trigger`}
                    className={`w-full flex items-center justify-between gap-2 px-3
                                ${compact ? 'h-10' : 'h-12'}
                                bg-surface border border-marigold/20 rounded-sm
                                text-parchment hover:border-marigold/50 transition-colors`}
                >
                    <span className="flex items-center gap-2 min-w-0">
                        <RelationIcon relation={selected?.relation} />
                        <span className="truncate text-sm">
                            <span className="text-parchment font-medium">{selected?.name}</span>
                            {selected?.relation && (
                                <span className="text-slate-500 text-xs ml-1.5">· {prettyRelation(selected.relation)}</span>
                            )}
                            {selected?.is_default && (
                                <span className="ml-2 text-marigold text-[10px] uppercase tracking-wider">default</span>
                            )}
                        </span>
                    </span>
                    <ChevronDown size={14} className="text-slate-500 shrink-0" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                className="w-72 bg-surface border-marigold/20 rounded-sm"
                data-testid={`${testIdPrefix}-menu`}
            >
                <DropdownMenuLabel className="text-xs uppercase tracking-wider text-slate-500">
                    Whose chart is this for?
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-marigold/10" />
                {profiles.map((p) => (
                    <DropdownMenuItem
                        key={p.id}
                        onClick={() => onChange?.(p.id, p)}
                        data-testid={`${testIdPrefix}-option-${p.id}`}
                        className="cursor-pointer focus:bg-marigold/10 text-parchment py-2"
                    >
                        <div className="flex items-center gap-2 w-full">
                            <RelationIcon relation={p.relation} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm truncate">{p.name}</div>
                                <div className="text-xs text-slate-500">
                                    {prettyRelation(p.relation)}
                                    {p.is_default && <span className="text-marigold ml-1.5">· default</span>}
                                </div>
                            </div>
                            {value === p.id && <Check size={14} className="text-marigold shrink-0" />}
                        </div>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}


function RelationIcon({ relation }) {
    switch (relation) {
        case 'self':    return <User size={14} className="text-marigold shrink-0" />;
        case 'spouse':  return <Heart size={14} className="text-marigold shrink-0" />;
        case 'child':   return <Baby size={14} className="text-marigold shrink-0" />;
        case 'parent':
        case 'sibling':
        case 'friend':
        case 'other':
        default:        return <UserCircle2 size={14} className="text-marigold shrink-0" />;
    }
}


export function prettyRelation(relation) {
    const map = {
        self: 'Self', spouse: 'Spouse', parent: 'Parent', child: 'Child',
        sibling: 'Sibling', friend: 'Friend', other: 'Other',
    };
    return map[relation] || relation;
}

export function RelationBadge({ relation }) {
    return (
        <span className="inline-flex items-center gap-1 text-xs text-slate-400">
            <RelationIcon relation={relation} />
            {prettyRelation(relation)}
        </span>
    );
}
